import { useCallback } from 'react';
import { useLanguageStore } from '../../../stores/languageStore';
import LanguageHelper from '../../../utils/languageHelper';
import type { Question } from '../survey.types';
import type { UseSurveyValidationReturn, ResponsesMap, ErrorsMap, ResponseValue } from '../SurveyPage.types';

/**
 * Custom hook for survey validation logic
 *
 * Provides validation functions for individual questions and entire surveys.
 * Uses the language store to provide localized error messages.
 *
 * @returns Object containing validation functions
 *
 * @example
 * const { validateQuestion, validateAllQuestions } = useSurveyValidation();
 * const error = validateQuestion(question, response);
 */
export const useSurveyValidation = (): UseSurveyValidationReturn => {
  const languageStore = useLanguageStore();

  /**
   * Validates a single question based on its requirements
   *
   * @param question - The question to validate
   * @param response - The user's response to validate
   * @returns Error message if validation fails, null if valid
   */
  const validateQuestion = useCallback((
    question: Question,
    response: ResponseValue | null | undefined
  ): string | null => {
    // Check for empty responses for required questions
    if (question.is_required) {
      if (response === undefined || response === null || response === '') {
        return languageStore.required;
      }

      // Validate array responses (multiple choice)
      if (Array.isArray(response) && response.length === 0) {
        return languageStore.pleaseSelect;
      }
    }

    // Validate multiple choice min/max selections
    if (question.question_type === 'multiple_choice' && Array.isArray(response)) {
      const selectionCount = response.length;

      // Check if "Other" is selected but no text provided
      const hasOtherSelected = response.some(r => r.startsWith('other:'));
      if (hasOtherSelected) {
        const otherResponse = response.find(r => r.startsWith('other:'));
        const otherText = otherResponse?.substring(6) || ''; // Remove "other:" prefix
        if (!otherText.trim()) {
          return LanguageHelper.getExtendedTranslation(languageStore.code, 'pleaseSpecifyOther');
        }
      }

      // Check minimum selections
      if (question.minSelectable && selectionCount < question.minSelectable) {
        return `${LanguageHelper.getExtendedTranslation(languageStore.code, 'pleaseSelectAtLeast')} ${question.minSelectable} ${LanguageHelper.getOptionText(languageStore.code, question.minSelectable)}`;
      }

      // Check maximum selections
      if (question.maxSelectable && selectionCount > question.maxSelectable) {
        return `${LanguageHelper.getExtendedTranslation(languageStore.code, 'pleaseSelectNoMoreThan')} ${question.maxSelectable} ${LanguageHelper.getOptionText(languageStore.code, question.maxSelectable)}`;
      }
    }

    // Validate dropdown questions
    if (question.question_type === 'dropdown' && typeof response === 'string') {
      // Check if "Other" is selected but no text provided
      if (response.startsWith('other:')) {
        const otherText = response.substring(6) || ''; // Remove "other:" prefix
        if (!otherText.trim()) {
          return LanguageHelper.getExtendedTranslation(languageStore.code, 'pleaseSpecifyOther');
        }
      }
    }

    // Validate email questions
    if (question.question_type === 'email' && typeof response === 'string') {
      const email = response.trim();
      if (email.length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          return LanguageHelper.getExtendedTranslation(languageStore.code, 'invalidEmail');
        }
      }
    }

    // Validate matrix questions - ensure all rows are answered
    if (question.question_type === 'matrix') {
      const matrixResponse = response as Record<number, number> | undefined;
      const expectedRows = question.questions.length;
      const answeredRows = matrixResponse ? Object.keys(matrixResponse).length : 0;

      if (answeredRows < expectedRows) {
        return languageStore.required;
      }
    }

    return null;
  }, [languageStore]);

  /**
   * Validates all questions in the survey
   *
   * @param questions - Array of all questions to validate
   * @param responses - Map of all user responses
   * @returns Map of question numbers to error messages (empty if all valid)
   */
  const validateAllQuestions = useCallback((
    questions: Question[],
    responses: ResponsesMap
  ): ErrorsMap => {
    const errors: ErrorsMap = {};

    questions.forEach((question) => {
      const response = responses[question.question_number];
      const error = validateQuestion(question, response);

      if (error) {
        errors[question.question_number] = error;
      }
    });

    return errors;
  }, [validateQuestion]);

  return {
    validateQuestion,
    validateAllQuestions,
  };
};
